import "./TeamPageV2.scss";
import { CheckOutlined, ReloadOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Input, Skeleton, Tabs, TabsProps } from "antd";
import { useRecoilState } from "recoil";
import { memberState } from "../../../../state/atoms";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MemberDto, MemberDtoRoleEnum, MemberLocationPermissionDto } from "../../../../generated/dashboard-client";
import AccountsApi from "../../../../api/AccountsApi";
import { UsersTable, UsersTableType } from "./UsersTable";
import { ResendInvitesModal } from "./ResendInvitesModal";

import UsersApi from "../../../../api/UsersApi";
import { success, warn } from "../../../../service/MessageService";
import { handleServerError } from "../../../../util/ErrorHandler";
import { useLocation, useNavigate } from "react-router-dom";
import { env } from "../../../../env/env";
import { debounce } from "lodash";
import MembersApi from "../../../../api/MembersApi";
import { isDesignSubscription, isProSubscription } from "../../../../service/BillingService";
import UserGroupAddIcon from "../../../icons/UserGroupAddIcon";
import ControlIcon from "../../../icons/ControlIcon";
import PermissionPresetsApi from "../../../../api/PermissionPresetsApi";
import { Preset, fromDto } from "./permissionModel";
import { NO_FILTERS, UserFilters, filterCount } from "./userAccess";
import { UserFilterDrawer } from "./UserFilterDrawer";
import { UserAccessDrawer } from "./UserAccessDrawer";

const { Search } = Input;

const RESEND_DELAY_MS = 1000;

export function TeamPageV2() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [member] = useRecoilState(memberState);
  const [users, setUsers] = useState([] as MemberDto[]);
  const [isFreeAccount, setIsFreeAccount] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("1");
  const [searchKey, setSearchKey] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [statusTotals, setStatusTotals] = useState({ active: 0, pending: 0, inactive: 0 });
  const [resendingAll, setResendingAll] = useState(false);
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [sentCount, setSentCount] = useState<number | null>(null);
  const [resentIds, setResentIds] = useState<Set<string>>(new Set());
  const [userPermissions, setUserPermissions] = useState<Record<string, MemberLocationPermissionDto[]>>({});
  const [filters, setFilters] = useState<UserFilters>(NO_FILTERS);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [accessUser, setAccessUser] = useState<MemberDto | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);
  const activeFilterCount = filterCount(filters);

  const debouncedSetSearchKey = useMemo(() => debounce((value: string) => {
    setSearchKey((value ?? "").trim());
    setCurrentPage(1);
    setPageSize(10);
  }, 300), []);

  const fetchData = useCallback(() => {
    if (!member.id || !member.plan || !member.internalId) return;
    setLoading(true);
    console.log("=> fetching data for currentPage=", currentPage, " pageSize=", pageSize, ", searchKey=", searchKey);

    let status = "active";
    if (activeTabKey === "2") {
      status = "pending";
    } else if (activeTabKey === "3") {
      status = "inactive";
    }

    AccountsApi.getUsersDetails(member.id, status, currentPage, pageSize, searchKey, filters.locationIds, filters.presetIds, filters.permissions).then((resp) => {
      setLoading(false);
      const owner = resp.data.owner ?? {};
      const found = resp.data.users ?? [];

      let includeOwner = currentPage === 1 && activeTabKey === "1" && activeFilterCount === 0;
      if (searchKey && activeFilterCount === 0) {
        includeOwner = owner.email!.toLowerCase().includes(searchKey.toLowerCase()) || owner.name!.toLowerCase().includes(searchKey.toLowerCase());
      }

      if (includeOwner) {
        setUsers([{
          id: owner.id ?? "owner",
          email: owner.email ?? "",
          name: owner.name ?? "",
          role: "Owner",
          invitationStatus: "Active"
        } as MemberDto, ...found]);
      } else {
        setUsers([...found]);
      }

      const total = resp.data.total ?? 0;
      setTotalUsers(total);
      setStatusTotals({
        active: resp.data.activeTotal ?? 0,
        pending: resp.data.pendingTotal ?? 0,
        inactive: resp.data.inactiveTotal ?? 0
      });

      if (!isProSubscription(member) && !member.diningRDIntegration) setIsFreeAccount(true);

      const userRoleIds = found.filter(u => u.role === MemberDtoRoleEnum.User && u.id).map(u => u.id!);
      if (userRoleIds.length > 0) {
        MembersApi.getBatchTeamLocationPermissions(userRoleIds).then(r => {
          setUserPermissions(r.data);
        }).catch(() => {
          setUserPermissions({});
        });
      } else {
        setUserPermissions({});
      }
    }).catch(() => {
      setLoading(false);
    });
  }, [member, currentPage, pageSize, searchKey, activeTabKey, filters, activeFilterCount]);

  useEffect(fetchData, [fetchData]);

  useEffect(() => {
    if (!member.teamId) return;
    PermissionPresetsApi.getPresets(member.teamId)
      .then((resp) => setPresets(resp.data.map(fromDto)))
      .catch(() => setPresets([]));
  }, [member.teamId]);

  function applyFilters(next: UserFilters) {
    setFilters(next);
    setCurrentPage(1);
  }

  function editPermissions(user: MemberDto) {
    setAccessUser(null);
    onMenuOptionClick("edit-permissions", user);
  }

  useEffect(() => {
    if (location.search.indexOf("pending") > -1) {
      setActiveTabKey("2");
    }
  }, [location]);

  function onMenuOptionClick(option: string, user: MemberDto) {
    if (option === "edit-locations") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/locations`);
    } else if (option === "edit-permissions") {
      navigate(env.dashboardPath + `/account/team/user/${user.id}/permissions`);
    } else if (option === "resend-invite") {
      setLoading(true);
      UsersApi.sendInvitationEmail(user.id || "").then(() => {
        success("Invitation sent!");
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "cancel-invite") {
      MembersApi.deleteUserTeamLocationPermissions(user.id!).then(() => { });
      UsersApi.revokeInvitation(user.id!).then(() => {
        success("Invitation revoked!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "remove-access") {
      setLoading(true);
      UsersApi.revokeUser(user.id!).then(() => {
        success("Access removed!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => { setLoading(false); });
    } else if (option === "activate-account") {
      if (user.role === MemberDtoRoleEnum.Admin) {
        MembersApi.updateUserLocationPermissions(user.id ?? "", {
          activate: true,
          locations: {}
        }).then(() => {
          success("Updated!");
          setUsers(users.filter((it) => it.id !== user.id));
        }).catch((err) => {
          handleServerError(err);
        });
      } else {
        navigate(env.dashboardPath + `/account/team/user/${user.id}/locations?activate=y`);
      }
    } else if (option === "permanently-remove") {
      setLoading(true);
      UsersApi.removeUser(user.id!).then(() => {
        success("User removed!");
        setUsers(users.filter((it) => it.id !== user.id));
      }).catch((e) => {
        handleServerError(e);
      }).finally(() => {
        setLoading(false);
      });
    }
  }

  function onResendAllClick() {
    if (!member.id || resendingAll) return;
    setResendConfirmOpen(true);
  }

  async function onConfirmResendAll() {
    if (!member.id || resendingAll) return;
    setResendConfirmOpen(false);
    setResendingAll(true);
    setSentCount(null);
    setResentIds(new Set());

    let pendingIds: string[] = [];
    try {
      const resp = await AccountsApi.getUsersDetails(member.id, "pending", 1, 1000, "");
      pendingIds = (resp.data.users ?? []).map((u) => u.id).filter((id): id is string => !!id);
    } catch (e) {
      handleServerError(e);
      setResendingAll(false);
      return;
    }

    let sent = 0;
    for (const id of pendingIds) {
      const ok = await UsersApi.sendInvitationEmail(id).then(() => true).catch(() => false);
      if (ok) {
        sent++;
        setResentIds((prev) => new Set(prev).add(id));
      }
      await new Promise((r) => setTimeout(r, RESEND_DELAY_MS));
    }

    setSentCount(sent);
    setResendingAll(false);

    const failed = pendingIds.length - sent;
    if (failed > 0) {
      warn(`${failed} of ${pendingIds.length} invitation${pendingIds.length === 1 ? "" : "s"} could not be resent.`);
    }
  }

  function sortUsers(key: string) {
    setUsers([...users].sort((a: MemberDto, b: MemberDto) => {
      if (!key) return 0;
      return key === "asc" ? (a.name ?? "").localeCompare(b.name ?? "") : (b.name ?? "").localeCompare(a.name ?? "")
    }))
  }

  function changeRole(member: MemberDto, role: MemberDtoRoleEnum) {
    if (member.role === role) return;

    MembersApi.updateRole(member.id!, role).then(() => {
      fetchData();
      if (role === MemberDtoRoleEnum.User) {
        navigate(env.dashboardPath + `/account/team/user/${member.id}/locations`);
      }
    })
  }

  const allUsersTotal = statusTotals.active + statusTotals.pending + statusTotals.inactive;
  const isNarrowed = searchKey.length > 0 || activeFilterCount > 0;

  const usersCountLine = allUsersTotal > 0 && (
    <div className={"results-count"}>
      <strong>
        {isNarrowed ? `${totalUsers} of ${allUsersTotal}` : allUsersTotal} total users
      </strong>
      {searchKey && <span className={"matching"}> matching “{searchKey}”</span>}
    </div>
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: `Active Users • ${statusTotals.active}`,
      children: <>{usersCountLine}<UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        onChangeRoleClick={changeRole}
        type={UsersTableType.Active}
        users={users}
        userPermissions={userPermissions}
        activeFilterCount={activeFilterCount}
        onFilterClick={() => setFilterDrawerOpen(true)}
        onAccessClick={setAccessUser}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      /></>
    },
    {
      key: "2",
      label: `Pending Invitations • ${statusTotals.pending}`,
      children: <>
        {usersCountLine}
        {totalUsers > 0 &&
          <div className={"pending-invites-bar"}>
            <span className={"pending-count"}>{totalUsers} pending invitation{totalUsers === 1 ? "" : "s"}</span>
            <Button
              className={"resend-all-btn"}
              icon={sentCount !== null ? <CheckOutlined /> : <ReloadOutlined />}
              loading={resendingAll}
              onClick={onResendAllClick}
            >
              {resendingAll
                ? "Resending…"
                : sentCount !== null
                  ? `${sentCount} invitation${sentCount === 1 ? "" : "s"} sent`
                  : "Resend All"}
            </Button>
          </div>}
        <UsersTable
          onSortClick={sortUsers}
          onMenuOptionClick={onMenuOptionClick}
          onChangeRoleClick={changeRole}
          type={UsersTableType.Pending}
          users={users}
          userPermissions={userPermissions}
          activeFilterCount={activeFilterCount}
          onFilterClick={() => setFilterDrawerOpen(true)}
          onAccessClick={setAccessUser}
          resentIds={resentIds}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalUsers,
            onChange: (page: number, pageSize: number) => {
              setCurrentPage(page);
              setPageSize(pageSize);
            }
          }}
        />
      </>
    },
    {
      key: "3",
      label: `Inactive Users • ${statusTotals.inactive}`,
      children: <>{usersCountLine}<UsersTable
        onSortClick={sortUsers}
        onMenuOptionClick={onMenuOptionClick}
        onChangeRoleClick={changeRole}
        type={UsersTableType.Inactive}
        users={users}
        userPermissions={userPermissions}
        activeFilterCount={activeFilterCount}
        onFilterClick={() => setFilterDrawerOpen(true)}
        onAccessClick={setAccessUser}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalUsers,
          onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          }
        }}
      /></>
    },
  ];

  // Free/Design-plan accounts must upgrade to Pro before managing their team. Returns true when the
  // upgrade modal was shown (so the caller should stop), false when the account may proceed.
  function requireProUpgrade(): boolean {
    if (!isFreeAccount) return false;
    if (isDesignSubscription(member)) {
      window.MHM.ProModal.switchToProWithOverview({
        memberId: member.id,
        onSuccess: () => window.location.reload()
      });
    } else {
      window.MHM.ProModal.upgrade({
        memberId: member.id,
        onSuccess: () => window.location.reload()
      });
    }
    return true;
  }

  function onNewUserClick() {
    if (requireProUpgrade()) return;
    navigate(env.dashboardPath + "/account/team/new");
  }

  function onBulkInviteClick() {
    if (requireProUpgrade()) return;
    navigate(env.dashboardPath + "/account/team/bulk-invite");
  }

  function onPermissionPresetsClick() {
    if (requireProUpgrade()) return;
    navigate(env.dashboardPath + "/account/team/permission-presets");
  }

  function onSearchKeyUp(target: HTMLInputElement) {
    debouncedSetSearchKey(target.value);
  }

  return (
    <div className={"content team-page-v2"}>
      <div className={"banner"}>
        <h3>My Team</h3>
        <h5>Work better together</h5>
        <div className={"users"}>
          <div className={"circle a"}><UserOutlined /></div>
          <div className={"circle b"}><UserOutlined /></div>
          <div className={"circle c"}><UserOutlined /></div>
        </div>
      </div>

      <header>
        <Button type={"primary"} className={"new"} onClick={onNewUserClick}>+ New User</Button>
        <Button className={"secondary"} icon={<UserGroupAddIcon />} onClick={onBulkInviteClick}>Bulk Invite</Button>
        <Button className={"secondary"} icon={<ControlIcon />} onClick={onPermissionPresetsClick}>Manage Permission Presets</Button>
        {(users.length > 0 || searchKey.length > 0 || activeFilterCount > 0) &&
          <Search className={"search"} onKeyUp={ev => onSearchKeyUp(ev.target as HTMLInputElement)}
            placeholder="Search users" prefix={<SearchOutlined />} addonAfter={<></>} />}
      </header>

      {loading && <Skeleton />}

      {!loading && <Tabs
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={items} />}

      {filterDrawerOpen && <UserFilterDrawer
        filters={filters}
        presets={presets}
        onApply={applyFilters}
        onClose={() => setFilterDrawerOpen(false)}
      />}

      {accessUser && <UserAccessDrawer
        user={accessUser}
        presets={presets}
        onEditPermissions={editPermissions}
        onClose={() => setAccessUser(null)}
      />}

      <ResendInvitesModal
        open={resendConfirmOpen}
        count={totalUsers}
        loading={resendingAll}
        onCancel={() => setResendConfirmOpen(false)}
        onConfirm={onConfirmResendAll}
      />
    </div>
  );
}
